import { createFileRoute } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { StatewideServiceHub } from "@/components/StatewideServiceHub";
import { buildCityCards } from "@/lib/statewideServiceCities";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildServiceHubScripts } from "@/lib/serviceHubSchema";

const CANONICAL = "https://coloradowebimpressions.com/web-design-near-me";
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({ service: "web-design" })}`;

export const Route = createFileRoute("/web-design-near-me")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      { title: "Website Design Near Me — Colorado's Local Web Design Team (2026)" },
      { name: "description", content: "Searching 'web design near me' in Colorado? Owner-led Colorado Springs web design with sub-2-second mobile sites built to convert local search traffic." },
      { property: "og:title", content: "Website Design Near Me — Colorado's Local Web Design Team (2026)" },
      { property: "og:description", content: "Searching 'web design near me' in Colorado? Owner-led Colorado Springs web design with sub-2-second mobile sites built to convert local search traffic." },
      { property: "og:url", content: CANONICAL },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Web Design Near Me — Colorado Springs & Statewide",
        description: "Owner-led local web design across Colorado since 2006.",
      }),
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: buildServiceHubScripts({
      path: "/web-design-near-me",
      name: "Local Web Design",
      description:
        "Local 'near me' web design for Colorado businesses. Owner-led design from a real Colorado Springs office, serving Denver, Springs, Boulder, Fort Collins, Pueblo, and the Front Range.",
      serviceType: "Web Design",
      priceRange: "$$",
      faqs: [
        {
          q: "Are you actually local to Colorado?",
          a: "Yes — we're headquartered at 3210 E Woodmen Rd in Colorado Springs and have served Colorado businesses since 2006. No offshore teams, no white-label resellers.",
        },
        {
          q: "Do you build websites outside Colorado Springs?",
          a: "Yes. We build for businesses across the entire state — Denver, Boulder, Fort Collins, Pueblo, Castle Rock, Parker, Monument, and every market in between.",
        },
        {
          q: "How much does a 'near me' web design project cost?",
          a: "Most Colorado small-business sites run $4,500–$12,000 one-time. We publish full price ranges on our pricing page — no quote games.",
        },
        {
          q: "How fast can you start?",
          a: "Discovery calls happen within 24 hours. Most projects kick off the same week and ship in 4–8 weeks.",
        },
      ],
    }),
  }),
  component: () => (
    <StatewideServiceHub
      service="Web Design"
      serviceLower="local web design"
      eyebrow="Website Design Near Me"
      headline="Searching 'website design near me' in Colorado? You found us."
      goldWord="found us"
      subheadline="Owner-led web design from a real Colorado Springs office. Serving Colorado businesses since 2006 — pick your city below and see exactly how we build for your market."
      benefits={[
        "Real Colorado office — 3210 E Woodmen Rd, Colorado Springs",
        "Owner-led since 2006 — no salespeople, no outsourcing",
        "Sub-2-second mobile loads on real 4G",
        "Custom-designed — never a template",
        "Free 24-hour quote, transparent pricing",
      ]}
      benefitIcon={MapPin}
      servicesGrid
      industriesGrid
      cities={buildCityCards("web-design", "/colorado-springs-web-design")}
      pickerHeading="Pick your Colorado city for local web design"
      closing="Don't see your city? We still build for you — every Colorado market is fair game. Reach out and we'll scope it."
    />
  ),
});
